/**
	* Tools view
	* Handles: tool picker and actual tools (e.g. draw tool)
	* @module Tools
*/

/// Imports

// React
import { useState, useEffect } from "react";

// OpenLayers
import OlMap from "ol/Map";

// Models
import MapToolMode from "lib/enums/maptoolmode";

// Views
import ZoomBox from "views/ZoomBox";
import ToolPicker from "views/tools/ToolPicker";
import DrawTool from "views/tools/DrawTool";
import MapLayerTool from "views/tools/MapLayerTool";
import PinDropTool from "views/tools/PinDropTool";
import BaseMapLayerTool from "views/tools/BaseMapLayerTool";
import MeasureTool from "views/tools/MeasureTool";
import AnnotationTool from "views/tools/AnnotationTool";
import SelectTool from "views/tools/SelectTool";
import RasterImageTool from "views/tools/RasterImageTool";
import ZoomBar from "views/ZoomBar";
import ScaleLine from "views/ScaleLine";
import LocalePicker from "views/LocalePicker";
import LocationSelect from 'views/LocationSelect';

// Tools
import { getMapZoom } from "lib/utilities/map";
import { getToolEnabled, getDrawingToolEnabled } from "lib/utilities/toolmodes";

interface ToolProps {
	olMap:OlMap
}

function Tools({olMap}:ToolProps) {
	const map:OlMap = olMap;
	const mapConfig:any = map.get('opus-config');
	
	// State hooks
	const [zoomLevel, setZoomLevel] = useState<number>(getMapZoom(map));
	const [currentToolMode, setCurrentToolMode] = useState<MapToolMode>(_config.defaultToolMode || MapToolMode.Select);
	const [shouldShowZoomBar, setShouldShowZoomBar] = useState<boolean>(_ui.getZoomBarEnabled(mapConfig));
	
	// Initialize for map
	useEffect(() => {
		/* 
			Set the master zoom level value when map is moved.
			Then this value is fed to the components that need it via props.
		*/
		map.on("moveend", (event:any) => {
			setZoomLevel(getMapZoom(map));
		});
		
		/*
			Event when tool modes changed
		*/
		_events.on('tool-modes-changed', () => {
			setShouldShowZoomBar(_ui.getZoomBarEnabled(mapConfig));
		});
		
		/*
			Select default tool mode on load (if defined)
		*/
		_config.defaultToolMode && _ui.invokeMapToolModeClickMaster(_config.defaultToolMode);
		
		/*
			A file has been dragged over the browser.
			So automatically set the tool mode to upload tool.
		*/
		window.addEventListener("dragenter", (e:DragEvent) => {
			e.preventDefault();
			
			_events.invoke("open-upload-popup");
		});
		
		window.addEventListener("dragover", (e:DragEvent) => {
			e.preventDefault();
		});
	}, []);
	
	// When the tool mode changes, re-create these events
	useEffect(() => {
		// These handlers are only for THIS view to accept
		
		/*
			Ctrl + A has been pressed (handled in views/DrawTool)
			So automatically set the tool mode to select tool.
		*/
		_events.unall("dt-selectall");
		_events.on("dt-selectall", () => {
			if(currentToolMode != MapToolMode.Select)
				onChangeTool(MapToolMode.Select);
		});
		
		/*
			Map has been idly clicked. So set the tool mode to the select tool.
			(Assumes user wants to select a map feature but are in the wrong tool mode)
		*/
		_events.unall("map-clicked");
		_events.on("map-clicked", () => {
			if(currentToolMode != MapToolMode.Select && !getDrawingToolEnabled(currentToolMode))
				onChangeTool(MapToolMode.Select);
		});
		
		/*
			Change tool mode from anywhere
		*/
		_events.unall("tool-mode-set");
		_events.on("tool-mode-set", (toolMode:MapToolMode) => {
			onChangeTool(toolMode);
		});
	}, [currentToolMode]);
	
	useEffect(() => setShouldShowZoomBar(_ui.getZoomBarEnabled(mapConfig)), [_ui.getZoomBarEnabled(mapConfig)]);
	
	const onChangeTool = (toolMode:MapToolMode) => {
		setCurrentToolMode(toolMode);
		
		_events.invoke("tool-mode-changed", toolMode);
	}
	
	_ui.currentMapToolMode = currentToolMode;
	
	return (
		<div className="tools-container">
			<ZoomBox olMap={olMap} zoomLevel={zoomLevel} currentToolMode={currentToolMode} />
			<ToolPicker olMap={olMap} currentToolMode={currentToolMode} onChangeTool={onChangeTool} />
			{shouldShowZoomBar && <ZoomBar olMap={olMap} zoomLevel={zoomLevel} />}
			{mapConfig.scaleBarEnabled && <ScaleLine olMap={olMap} />}
			<LocalePicker olMap={olMap} />
			<LocationSelect olMap={olMap} />
			
			<DrawTool olMap={olMap} zoomLevel={zoomLevel} currentToolMode={currentToolMode} />
			<MeasureTool olMap={olMap} currentToolMode={currentToolMode} />
			<AnnotationTool olMap={olMap} currentToolMode={currentToolMode} />
			<BaseMapLayerTool olMap={olMap} currentToolMode={currentToolMode} />
			<MapLayerTool olMap={olMap} currentToolMode={currentToolMode} />
			<PinDropTool olMap={olMap} currentToolMode={currentToolMode} />
			<RasterImageTool olMap={olMap} currentToolMode={currentToolMode} />
			<SelectTool olMap={olMap} currentToolMode={currentToolMode} />
		</div>
	)
}

export default Tools;